import { z } from 'zod'

import { CasAssignedStudentSchema, CasAssignmentStatusSchema, CasTypeSchema } from '~/@entities/Cas'
import { ShareScopeSchema } from '~/@entities/Content'
import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { StudentIdSchema } from '~/@entities/Student'
import { WorkbookTypeSchema } from '~/@entities/Workbook'
import { WorksheetTagSchema } from '~/@entities/Worksheet'

/***************** 보고서 종류 *****************/
export const REPORT_TYPE = [
  'MONTHLY',
  'CUSTOM_PERIOD',
  'STUDENT_ACHIEVEMENT',
  'CUSTOM',
  'REAL_TIME',
] as const

/***************** 보고서 구성 섹션 *****************/
// 서버 ReportComponentV2
export const REPORT_COMPONENT = [
  'COVER',
  'MONTHLY',
  'COMMENT',
  'WEEKLY',
  'LEVEL',
  'STUDY_HISTORY',
  'STUDY_PERFORMANCE',
  'SCORING_RESULT',
  'STUDY_EVALUATION',
] as const

// 구 보고서(/report)의 구성 섹션. 서버도 V2 와 따로 둔다.
export const LEGACY_REPORT_COMPONENT = [
  'WEEKLY',
  'CHAPTER_SUMMARY',
  'LEVEL',
  'STUDY_HISTORY',
  'CHAPTER_DETAIL',
  'ASSESSMENT',
] as const

/***************** 비교군 *****************/
// 서버 ReportComparisonGroup. LESSON_CLASS_OR_GRADE(반이 있으면 반, 없으면 학년)는 서버가 지울 예정이라 앱이
// 고르지 않지만 예전에 저장된 설정이 그 값으로 올 수 있다.
export const REPORT_COMPARISON_GROUP = [
  'NATIONAL',
  'GRADE',
  'LESSON_CLASS',
  'LESSON_CLASS_OR_GRADE',
] as const

// 영역별 분석은 학원 안의 비교군만 쓴다
export const STUDY_EVALUATION_COMPARISON_GROUP = ['GRADE', 'LESSON_CLASS'] as const

/***************** 학생 묶음 단위 *****************/
export const REPORT_STUDENT_GROUP = ['STUDENT', 'GRADE', 'LESSON_CLASS'] as const

/***************** PDF 생성 상태 *****************/
export const REPORT_PDF_STATUS = ['GENERATING', 'SUCCESS', 'FAIL'] as const

/***************** 행동영역 *****************/
// 15개정(계산·이해·추론·문제해결)과 22개정 역량(문제해결·추론·의사소통·연결·정보처리)을 서버는 한 enum 으로 둔다
export const BEHAVIOR_AREA = [
  'CALCULATION_ABILITY',
  'COMPREHENSION_ABILITY',
  'REASONING_ABILITY',
  'PROBLEM_SOLVING_ABILITY',
  'COMMUNICATION_ABILITY',
  'CONNECTION_ABILITY',
  'INFORMATION_PROCESSING_ABILITY',
] as const

// 개정마다 보고서가 보여 주는 행동영역과 그 순서
export const BEHAVIOR_AREA_15 = [
  'CALCULATION_ABILITY',
  'COMPREHENSION_ABILITY',
  'REASONING_ABILITY',
  'PROBLEM_SOLVING_ABILITY',
] as const satisfies readonly (typeof BEHAVIOR_AREA)[number][]
export const BEHAVIOR_AREA_22 = [
  'PROBLEM_SOLVING_ABILITY',
  'REASONING_ABILITY',
  'COMMUNICATION_ABILITY',
  'CONNECTION_ABILITY',
  'INFORMATION_PROCESSING_ABILITY',
] as const satisfies readonly (typeof BEHAVIOR_AREA)[number][]

export const ReportTypeSchema = z.enum(REPORT_TYPE)
export type ReportType = z.infer<typeof ReportTypeSchema>

export const ReportComponentSchema = z.enum(REPORT_COMPONENT)
export type ReportComponent = z.infer<typeof ReportComponentSchema>

export const LegacyReportComponentSchema = z.enum(LEGACY_REPORT_COMPONENT)
export type LegacyReportComponent = z.infer<typeof LegacyReportComponentSchema>

export const ReportComparisonGroupSchema = z.enum(REPORT_COMPARISON_GROUP)
export type ReportComparisonGroup = z.infer<typeof ReportComparisonGroupSchema>

export const StudyEvaluationComparisonGroupSchema = z.enum(STUDY_EVALUATION_COMPARISON_GROUP)
export type StudyEvaluationComparisonGroup = z.infer<typeof StudyEvaluationComparisonGroupSchema>

export const ReportStudentGroupSchema = z.enum(REPORT_STUDENT_GROUP)
export type ReportStudentGroup = z.infer<typeof ReportStudentGroupSchema>

export const ReportPdfStatusSchema = z.enum(REPORT_PDF_STATUS)
export type ReportPdfStatus = z.infer<typeof ReportPdfStatusSchema>

export const BehaviorAreaSchema = z.enum(BEHAVIOR_AREA)
export type BehaviorArea = z.infer<typeof BehaviorAreaSchema>
export type BehaviorArea15 = (typeof BEHAVIOR_AREA_15)[number]
export type BehaviorArea22 = (typeof BEHAVIOR_AREA_22)[number]

const _date = z.iso.date()
const _localDatetime = z.iso.datetime({ local: true })

/** 보고서가 다루는 대단원 */
export const ReportChapterSchema = z.object({
  revision: RevisionSchema,
  chapter: z.string(),
})
export type ReportChapter = z.infer<typeof ReportChapterSchema>

/** 기간 전체의 요약 수치. 채점 기록이 없으면 null 이다 */
export const ReportMetricSchema = z.object({
  scoringCount: z.number().nullable(),
  rank: z.number().nullable(),
  correctRate: z.number().nullable(),
})
export type ReportMetric = z.infer<typeof ReportMetricSchema>

/***************** 보고서 본문 *****************/
// 서버 ReportRequestInterface. 채점 조회가 내고 앱이 편집해 생성·수정 요청에 그대로 다시 싣고, PDF 렌더러가 그린다.

const _MonthStatisticsSchema = z.object({
  year: z.number(),
  month: z.number(),
  student: z.number(),
  comparison: z.number().nullable(),
})
const _WeekStatisticsSchema = _MonthStatisticsSchema.extend({ week: z.number() })
const _LevelStatisticsSchema = z.object({
  level: z.number(),
  student: z.number(),
  comparison: z.number().nullable(),
})

export const ReportConceptSchema = z.object({
  id: z.number(),
  orderingNumber: z.number(),
  name: z.string(),
  correctRate: z.object({ student: z.number(), comparison: z.number().nullable() }),
})
export type ReportConcept = z.infer<typeof ReportConceptSchema>

const _StudyRecordSchema = z.object({
  title: z.string(),
  titlePrefix: z.string(),
  scoringCount: z.number(),
  correctRate: z.number(),
})

export const ReportDocumentSchema = z.object({
  components: z.array(ReportComponentSchema),
  startDate: _date,
  endDate: _date,
  year: z.number(),
  month: z.number(),
  title: z.string(),
  studentSchoolType: SchoolTypeSchema,
  studentGrade: z.number(),
  studentName: z.string(),
  logoUrl: z.string().nullable(),
  comparisonType: ReportComparisonGroupSchema.exclude(['LESSON_CLASS_OR_GRADE']).nullable(),
  studyEvaluationComparisonType: StudyEvaluationComparisonGroupSchema.nullable(),
  // 보고서 기간 전체
  rank: z.number(),
  correctRate: z.number(),
  scoringCount: z.number(),
  level: z.number(),
  monthlyComponent: z.object({
    ranksPerMonth: z.array(_MonthStatisticsSchema),
    correctRatesPerMonth: z.array(_MonthStatisticsSchema),
    scoringCountsPerMonth: z.array(_MonthStatisticsSchema),
    levelsPerMonth: z.array(_MonthStatisticsSchema),
  }),
  commentComponent: z.object({ comment: z.string().nullable() }).nullable(),
  weeklyComponent: z.object({
    ranksPerWeek: z.array(_WeekStatisticsSchema),
    correctRatesPerWeek: z.array(_WeekStatisticsSchema),
    scoringCountsPerWeek: z.array(_WeekStatisticsSchema),
    levelsPerWeek: z.array(_WeekStatisticsSchema),
    scoringCountsPerLevel: z.array(_LevelStatisticsSchema),
    correctRatesPerLevel: z.array(_LevelStatisticsSchema),
  }),
  studyHistoryComponent: z.object({
    scoringCountsPerStudyType: z.object({
      workbook: z.number(),
      worksheet: z.number(),
      levelUpOrWrongAnswer: z.number(),
      challenge: z.number(),
      cas: z.number(),
      total: z.number(),
    }),
    workbooks: z.array(
      z.object({
        type: WorkbookTypeSchema,
        title: z.string(),
        shareScope: ShareScopeSchema.nullable(),
        scoringStartPageNumber: z.number(),
        scoringEndPageNumber: z.number(),
        scoringCount: z.number(),
        correctRate: z.number(),
      }),
    ),
    worksheets: z.array(_StudyRecordSchema.extend({ scoringDatetime: _date })),
    levelUpOrWrongAnswers: z.array(_StudyRecordSchema.extend({ learningDatetime: _date })),
    challenges: z.array(_StudyRecordSchema.extend({ learningDatetime: _date })),
    cases: z.array(_StudyRecordSchema.extend({ scoringDatetime: _date })),
  }),
  studyPerformanceComponent: z.object({
    middleChapters: z.array(
      z.object({
        revision: RevisionSchema,
        name: z.string(),
        scoringCount: z.number(),
        correctRate: z.number(),
      }),
    ),
  }),
  scoringResultComponent: z.object({
    middleChapters: z.array(
      z.object({
        revision: RevisionSchema,
        name: z.string(),
        scoringCount: z.number(),
        correctRate: z.number(),
        scoring: z.object({
          repConcept: z.object({
            best: z.array(ReportConceptSchema),
            worst: z.array(ReportConceptSchema),
          }),
          concepts: z.array(ReportConceptSchema),
        }),
      }),
    ),
  }),
  studyEvaluationComponent: z.object({
    studyEvaluationSections: z.array(
      z.object({
        revision: RevisionSchema,
        schoolType: SchoolTypeSchema,
        behaviorSections: z.array(
          z.object({
            type: BehaviorAreaSchema,
            studentRank: z.number(),
            studentCorrectRate: z.number(),
            comparisonCorrectRate: z.number().nullable(),
          }),
        ),
      }),
    ),
  }),
})
export type ReportDocument = z.infer<typeof ReportDocumentSchema>

/***************** 보고서 목록 *****************/

/** 실시간 보고서(이번 달 진행 중인 보고서) 한 건 */
export const RealTimeReportSchema = z.object({
  type: z.literal('REAL_TIME'),
  year: z.number(),
  month: z.number(),
  startDate: _date,
  endDate: _date,
  title: z.string(),
  createdAt: _localDatetime,
  studentId: StudentIdSchema,
  studentName: z.string(),
})
export type RealTimeReport = z.infer<typeof RealTimeReportSchema>

/** 만든 보고서 한 건 */
export const CustomReportSchema = z.object({
  id: z.number(),
  type: ReportTypeSchema.exclude(['REAL_TIME']),
  year: z.number(),
  month: z.number(),
  startDate: _date,
  endDate: _date,
  title: z.string(),
  metric: ReportMetricSchema,
  createdAt: _localDatetime,
  studentId: StudentIdSchema,
  studentName: z.string(),
  pdfStatus: ReportPdfStatusSchema,
  sharedToParent: z.boolean(),
  // 올림앱(크레버스 학부모앱) 공유 이력이 없으면 null
  reportIsOpen: z.boolean().nullable(),
  chapters: z.array(ReportChapterSchema),
  behaviorAreaAverageScore: z.number().nullable(),
  contentAreaAverageScore: z.number().nullable(),
})
export type CustomReport = z.infer<typeof CustomReportSchema>

/***************** 실시간 보고서 설정 *****************/

// worksheetTags·workbookTypes 의 null 은 "전체 선택" 이다. casTypes 는 이 관례를 따르지 않는다 — CAS 는 일부 학원에만
// 있는 기능이라 null 을 전체로 읽으면 권한 없는 학원에 CAS 기록이 끼어든다. 빈 배열이 미선택이다.
export const RealTimeReportSettingSchema = z.object({
  used: z.boolean(),
  worksheetTags: z.array(WorksheetTagSchema).nullable(),
  workbookTypes: z.array(WorkbookTypeSchema).nullable(),
  includeSelfLearning: z.boolean(),
  components: z.array(ReportComponentSchema),
  comparisonGroup: ReportComparisonGroupSchema.nullable(),
  casTypes: z.array(CasTypeSchema).nullable(),
})
export type RealTimeReportSetting = z.infer<typeof RealTimeReportSettingSchema>

/***************** 채점 이력 *****************/

const _ScoringHistoryBaseSchema = z.object({
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  title: z.string(),
  lastScoringDate: _date,
  scoringCount: z.number(),
  correctRate: z.number(),
})

export const WorksheetScoringHistorySchema = _ScoringHistoryBaseSchema.extend({
  studentWorksheetId: z.number(),
  titlePrefix: z.string(),
  autoScored: z.boolean(),
})
export type WorksheetScoringHistory = z.infer<typeof WorksheetScoringHistorySchema>

export const WorkbookScoringHistorySchema = _ScoringHistoryBaseSchema.extend({
  studentWorkbookId: z.number(),
  semester: SemesterSchema,
  autoScored: z.boolean(),
  scoringStartPage: z.number(),
  scoringEndPage: z.number(),
})
export type WorkbookScoringHistory = z.infer<typeof WorkbookScoringHistorySchema>

export const CasScoringHistorySchema = _ScoringHistoryBaseSchema.extend({
  assignedCasId: CasAssignedStudentSchema.shape.assignedId,
  status: CasAssignmentStatusSchema,
})
export type CasScoringHistory = z.infer<typeof CasScoringHistorySchema>

/** 보고서에 담을 학습을 고르는 목록 — 기간 안에 채점한 학습지·교재 종류별·자동시험 */
export const ReportScoringHistorySchema = z.object({
  worksheets: z.array(WorksheetScoringHistorySchema),
  customWorkbooks: z.array(WorkbookScoringHistorySchema),
  publicWorkbooks: z.array(WorkbookScoringHistorySchema),
  schoolWorkbooks: z.array(WorkbookScoringHistorySchema),
  customSignatureWorkbooks: z.array(WorkbookScoringHistorySchema),
  academyGroupWorkbooks: z.array(WorkbookScoringHistorySchema),
  cases: z.array(CasScoringHistorySchema),
})
export type ReportScoringHistory = z.infer<typeof ReportScoringHistorySchema>

export const ReportScoringHistoryCountSchema = z.object({
  worksheetCount: z.number(),
  workbookCount: z.number(),
  casCount: z.number(),
})
export type ReportScoringHistoryCount = z.infer<typeof ReportScoringHistoryCountSchema>

/***************** 보고서 만들기 *****************/

/** 고른 학습으로 만든 보고서 본문 초안 */
export const ReportDraftSchema = z.object({
  request: ReportDocumentSchema,
  chapters: z.array(ReportChapterSchema),
})
export type ReportDraft = z.infer<typeof ReportDraftSchema>

/**
 * 수정 진입 시 기존 보고서의 본문과 학습 선택. 서버는 생성·수정 요청이 받는 casTypes·aiCommentUsed·
 * additionalScore 를 이 응답에 싣지 않는다.
 */
export const CustomReportEditSourceSchema = ReportDraftSchema.extend({
  studentWorksheetIds: z.array(z.number()).nullable(),
  studentWorkbookIds: z.array(z.number()).nullable(),
  assignedCasIds: z.array(CasAssignedStudentSchema.shape.assignedId).nullable(),
  worksheetTags: z.array(WorksheetTagSchema).nullable(),
  workbookTypes: z.array(WorkbookTypeSchema).nullable(),
  includeSelfLearning: z.boolean(),
  comparisonGroup: ReportComparisonGroupSchema.nullable(),
})
export type CustomReportEditSource = z.infer<typeof CustomReportEditSourceSchema>

/** 보고서 공유 링크 */
export const ReportShareLinkSchema = z.object({
  linkId: z.string(),
  link: z.string(),
})
export type ReportShareLink = z.infer<typeof ReportShareLinkSchema>

/** 내려받기·미리보기용 서명 URL */
export const ReportFileSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type ReportFile = z.infer<typeof ReportFileSchema>

/***************** 구 보고서 *****************/
// 서버 YearMonth 는 yyyy-MM 문자열로 직렬화된다
const _yearMonth = z.string().regex(/^\d{4}-\d{2}$/)

/** 구 보고서(월별·기간) 한 건 */
export const LegacyReportSchema = z.object({
  id: z.number(),
  studentId: StudentIdSchema,
  studentName: z.string().nullable(),
  type: ReportTypeSchema,
  revision: RevisionSchema,
  yearMonth: _yearMonth,
  startDate: _date,
  endDate: _date,
  title: z.string(),
  status: ReportPdfStatusSchema,
  comment: z.string(),
  chapter: z.string().nullable(),
  extraScore: z.number(),
  areaScore: z.number(),
  behaviorScore: z.number().nullable(),
  totalScore: z.number().nullable(),
  totalTier: z.number().nullable(),
  componentFlags: z.array(LegacyReportComponentSchema),
  worksheetTagFlags: z.array(WorksheetTagSchema),
  selfLearningFlag: z.boolean(),
  // 학부모앱 공개 여부와 공개하면서 문자를 보냈는지
  opened: z.boolean(),
  sending: z.boolean(),
  deleted: z.boolean(),
  pdfUrl: z.string().nullable(),
  createDatetime: _localDatetime,
})
export type LegacyReport = z.infer<typeof LegacyReportSchema>

/** 월별 보고서를 그 달로 만들 수 있는지 */
export const MonthlyReportAvailabilitySchema = z.object({
  yearMonth: _yearMonth,
  available: z.boolean(),
})
export type MonthlyReportAvailability = z.infer<typeof MonthlyReportAvailabilitySchema>

/** 구 보고서 생성·수정·중복 확인 결과. 같은 기간 보고서가 있으면 다시 만들어진다 */
export const LegacyReportSaveResultSchema = z.object({
  recreated: z.boolean(),
  recreatedStudentIds: z.array(StudentIdSchema).nullable(),
  smsSendingFailedStudentIds: z.array(StudentIdSchema),
})
export type LegacyReportSaveResult = z.infer<typeof LegacyReportSaveResultSchema>

/** 가장 최근에 만든 보고서의 연월 */
export const RecentReportYearMonthSchema = z.object({
  year: z.number(),
  month: z.number(),
})
export type RecentReportYearMonth = z.infer<typeof RecentReportYearMonthSchema>

/***************** 유형분석 보고서 *****************/

/** 학생 한 명의 유형분석 보고서. PDF 는 서버가 비동기로 만들고, 크레버스 학원은 학부모 공유 여부를 함께 싣는다 */
export const AchievementReportSchema = z.object({
  id: z.number(),
  title: z.string(),
  comment: z.string(),
  createDatetime: _localDatetime,
  pdfUrl: z.string(),
  status: ReportPdfStatusSchema,
  creverseShared: z.boolean().nullable(),
})
export type AchievementReport = z.infer<typeof AchievementReportSchema>

export const StudentAchievementReportsSchema = z.object({
  studentId: StudentIdSchema,
  studentName: z.string(),
  reportInfo: z.array(AchievementReportSchema),
})
export type StudentAchievementReports = z.infer<typeof StudentAchievementReportsSchema>

/***************** AI 코멘트 (AI 서버) *****************/
// AI 서버(ai.<도메인>)는 Swagger 를 내지 않고 소스도 이 저장소에서 볼 수 없다. 2026-10-14 기준 선생님 앱이 응답에서
// 읽던 필드를 근거로 적었다. 응답은 CommonResponse 봉투 없이 본문 그대로다.

/** 유형분석 보고서 AI 코멘트를 스트리밍으로 받기 전에 여는 세션 */
export const ReportAiSessionSchema = z.object({ sessionId: z.string() })
export type ReportAiSession = z.infer<typeof ReportAiSessionSchema>

/** 보고서 지표로 AI 가 쓴 코멘트 */
export const ReportAiCommentSchema = z.object({ comment: z.string() })
export type ReportAiComment = z.infer<typeof ReportAiCommentSchema>
